import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Activity, Thermometer, Zap, TrendingUp, Battery } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AssetDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [socHistory, setSocHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartsReady, setChartsReady] = useState(false);
  const [engineerInstallers, setEngineerInstallers] = useState([]);
  const [user, setUser] = useState(null);
  const [weatherData, setWeatherData] = useState(null);

  useEffect(() => {
    loadAssetData();
  }, [id]);

  const loadAssetData = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (!token || !userData) {
        navigate('/login');
        return;
      }

      setUser(JSON.parse(userData));
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [assetRes, analyticsRes, telemetryRes, socHistoryRes, installersRes, weatherRes] = await Promise.all([
        axios.get(`${API}/assets/${id}`, config),
        axios.get(`${API}/analytics/asset/${id}`, config),
        axios.get(`${API}/telemetry/${id}?limit=50`, config),
        axios.get(`${API}/telemetry/soc-history/${id}?days=100`, config).catch(err => {
          console.error('SOC history fetch failed:', err);
          return { data: [] };
        }),
        axios.get(`${API}/assets/config/engineer-installers`, config),
        axios.get(`${API}/weather/${id}`, config).catch(err => {
          console.error('Weather fetch failed:', err);
          return { data: null };
        })
      ]);

      setAsset(assetRes.data);
      setAnalytics(analyticsRes.data);
      setTelemetry(telemetryRes.data.reverse());
      console.log('SOC History Response:', socHistoryRes.data);
      console.log('SOC History Length:', socHistoryRes.data?.length || 0);
      setSocHistory(socHistoryRes.data || []);
      setEngineerInstallers(installersRes.data.installers || []);
      setWeatherData(weatherRes.data);
      
      // Delay chart rendering to ensure proper hydration
      setTimeout(() => {
        setChartsReady(true);
      }, 100);
    } catch (error) {
      toast.error('Failed to load asset data');
    } finally {
      setLoading(false);
    }
  };

  const generateTelemetry = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/telemetry/generate/${id}?count=50`, {}, config);
      toast.success('Telemetry generated successfully');
      loadAssetData();
    } catch (error) {
      toast.error('Failed to generate telemetry');
    }
  };

  const handleCheckboxUpdate = async (field, value) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/assets/${id}/checkboxes?${field}=${value}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsset(prev => ({ ...prev, [field]: value }));
      toast.success('Asset updated successfully');
    } catch (error) {
      console.error('Checkbox update error:', error);
      toast.error('Failed to update asset');
      // Reload asset data to revert UI
      loadAssetData();
    }
  };

  const handleFieldUpdate = async (field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { 
        [field]: (field === 'warranty_degradation_rate' || field === 'actual_degradation_rate') 
          ? parseFloat(value) 
          : value 
      };
      
      await axios.patch(`${API}/assets/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAsset(prev => ({ ...prev, [field]: payload[field] }));
      toast.success('Asset updated successfully');
    } catch (error) {
      console.error('Field update error:', error);
      toast.error('Failed to update asset');
      loadAssetData();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-black">Asset not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8" data-testid="asset-detail-page">
      <div className="max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black mb-6"
          onClick={() => navigate('/dashboard')}
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Asset Header */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {asset.name}
                </h1>
                <Badge>{asset.asset_type}</Badge>
              </div>
              <p className="text-gray-600">{asset.location}</p>
              <p className="text-sm text-gray-600 mt-1">Serial: {asset.serial_no}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-500 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {asset.health_score.toFixed(2)}%
              </div>
              <p className="text-sm text-gray-600">Health Score</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div>
              <p className="text-xs text-gray-600 mb-1">Model</p>
              <p className="text-black font-medium">{asset.model}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Capacity</p>
              <p className="text-black font-medium">{asset.rated_capacity_kwh} kWh</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Voltage</p>
              <p className="text-black font-medium">{asset.nominal_voltage}V</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Status</p>
              <Badge variant="outline" className="text-emerald-500 border-emerald-500">{asset.status}</Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-xs text-gray-600 mb-1">Manufacture Date</p>
              <p className="text-black font-medium">{formatDate(asset.manufacture_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Invoice Date</p>
              <p className="text-black font-medium">{formatDate(asset.invoice_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Commissioning Date</p>
              <p className="text-black font-medium">{formatDate(asset.commissioning_date)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Safety Score</p>
              <p className="text-black font-medium">{asset.safety_score.toFixed(2)}%</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-xs text-gray-600 mb-2">Warranty Degradation</p>
              {user?.role === 'admin' ? (
                <select
                  value={asset.warranty_degradation_rate}
                  onChange={(e) => handleFieldUpdate('warranty_degradation_rate', e.target.value)}
                  className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded text-black font-medium"
                >
                  <option value="1">1% per year</option>
                  <option value="1.5">1.5% per year</option>
                  <option value="2">2% per year</option>
                  <option value="2.5">2.5% per year</option>
                  <option value="3">3% per year</option>
                  <option value="3.5">3.5% per year</option>
                  <option value="4">4% per year</option>
                  <option value="4.5">4.5% per year</option>
                  <option value="5">5% per year</option>
                </select>
              ) : (
                <p className="text-black font-medium">{asset.warranty_degradation_rate}% per year</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-2">Actual Degradation</p>
              {user?.role === 'admin' ? (
                <select
                  value={asset.actual_degradation_rate || 1.0}
                  onChange={(e) => handleFieldUpdate('actual_degradation_rate', e.target.value)}
                  className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded text-black font-medium"
                >
                  <option value="1">1% per year</option>
                  <option value="1.5">1.5% per year</option>
                  <option value="2">2% per year</option>
                  <option value="2.5">2.5% per year</option>
                  <option value="3">3% per year</option>
                  <option value="3.5">3.5% per year</option>
                  <option value="4">4% per year</option>
                  <option value="4.5">4.5% per year</option>
                  <option value="5">5% per year</option>
                  <option value="5.5">5.5% per year</option>
                  <option value="6">6% per year</option>
                  <option value="6.5">6.5% per year</option>
                  <option value="7">7% per year</option>
                  <option value="7.5">7.5% per year</option>
                  <option value="8">8% per year</option>
                </select>
              ) : (
                <p className="text-black font-medium">{asset.actual_degradation_rate || 1.0}% per year</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">State of Health (SOH)</p>
              <p className={`font-semibold text-lg ${asset.soh >= 95 ? 'text-emerald-600' : asset.soh >= 90 ? 'text-yellow-600' : 'text-red-600'}`}>
                {asset.soh.toFixed(2)}%
              </p>
            </div>
            <div className="md:col-span-1">
              <p className="text-xs text-gray-600 mb-2">Engineer Installer</p>
              {user?.role === 'admin' ? (
                <select
                  value={asset.engineer_installer}
                  onChange={(e) => handleFieldUpdate('engineer_installer', e.target.value)}
                  className="w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded text-black font-medium"
                >
                  {engineerInstallers.map((installer) => (
                    <option key={installer} value={installer}>{installer}</option>
                  ))}
                </select>
              ) : (
                <p className="text-black font-medium text-sm">{asset.engineer_installer || 'N/A'}</p>
              )}
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-600 mb-3">Installation & Delivery Verification</p>
            <div className="flex flex-col space-y-2">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={asset.installed_per_manual}
                  onChange={(e) => handleCheckboxUpdate('installed_per_manual', e.target.checked)}
                  disabled={user?.role === 'guest'}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">Product installed in accordance with Operations Manual?</span>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={asset.received_undamaged}
                  onChange={(e) => handleCheckboxUpdate('received_undamaged', e.target.checked)}
                  disabled={user?.role === 'guest'}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">Unit received at site before commissioning undamaged?</span>
              </label>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                State of Charge
                <Battery className="w-5 h-5 text-emerald-500" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {(analytics?.kpis?.soc || asset.soc).toFixed(2)}%
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Efficiency
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {(analytics?.kpis?.efficiency || 92).toFixed(2)}%
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center justify-between">
                Temperature
                <Thermometer className="w-5 h-5 text-emerald-500" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {(analytics?.kpis?.current_temp || 25).toFixed(2)}°C
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 10-Year Degradation Projection - First Chart */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-black">10-Year Degradation Projection from Commissioning</CardTitle>
          </CardHeader>
          <CardContent>
            <svg 
              key={`large-graph-${asset.id}-${asset.warranty_degradation_rate}-${asset.actual_degradation_rate}`}
              width="100%" 
              height="300" 
              viewBox="0 0 800 300" 
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Background grid */}
              <defs>
                <pattern id="degradation-grid" width="80" height="30" patternUnits="userSpaceOnUse">
                  <path d="M 80 0 L 0 0 0 30" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="800" height="300" fill="url(#degradation-grid)" />
              
              {/* Y-axis (SOH %) */}
              <line x1="60" y1="20" x2="60" y2="260" stroke="#6b7280" strokeWidth="2" />
              <text x="25" y="30" fill="#000" fontSize="14" fontWeight="bold">100%</text>
              <text x="30" y="95" fill="#6b7280" fontSize="14">90%</text>
              <text x="30" y="145" fill="#6b7280" fontSize="14">80%</text>
              <text x="30" y="195" fill="#6b7280" fontSize="14">70%</text>
              <text x="30" y="245" fill="#6b7280" fontSize="14">60%</text>
              <text x="30" y="265" fill="#6b7280" fontSize="14">50%</text>
              <text x="10" y="150" fill="#000" fontSize="12" fontWeight="bold" transform="rotate(-90, 10, 150)">State of Health (%)</text>
              
              {/* X-axis (Years) */}
              <line x1="60" y1="260" x2="760" y2="260" stroke="#6b7280" strokeWidth="2" />
              <text x="60" y="280" fill="#6b7280" fontSize="12">Year 0</text>
              <text x="195" y="280" fill="#6b7280" fontSize="12">Year 2</text>
              <text x="335" y="280" fill="#6b7280" fontSize="12">Year 4</text>
              <text x="475" y="280" fill="#6b7280" fontSize="12">Year 6</text>
              <text x="615" y="280" fill="#6b7280" fontSize="12">Year 8</text>
              <text x="735" y="280" fill="#6b7280" fontSize="12">Year 10</text>
              <text x="370" y="295" fill="#000" fontSize="12" fontWeight="bold">Time (Years from Commissioning)</text>
              
              {/* Grid lines */}
              {[0, 2, 4, 6, 8, 10].map((year) => {
                const x = 60 + (year * 70);
                return <line key={`vline-${year}`} x1={x} y1="260" x2={x} y2="20" stroke="#e5e7eb" strokeWidth="1" />;
              })}
              {[100, 90, 80, 70, 60, 50].map((soh) => {
                const y = 260 - ((soh - 50) * 4.8);
                return <line key={`hline-${soh}`} x1="60" y1={y} x2="760" y2={y} stroke="#e5e7eb" strokeWidth="1" />;
              })}
              
              {/* Warranty Degradation line (Green) */}
              <polyline
                points={(() => {
                  const degradationRate = asset.warranty_degradation_rate;
                  const points = [];
                  for (let year = 0; year <= 10; year++) {
                    const x = 60 + (year * 70);
                    const soh = Math.max(50, 100 - (year * degradationRate));
                    const y = 260 - ((soh - 50) * 4.8);
                    points.push(`${x},${y}`);
                  }
                  return points.join(' ');
                })()}
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
              />
              
              {/* Actual Degradation line (Orange/Red, dashed) */}
              <polyline
                points={(() => {
                  const actualRate = asset.actual_degradation_rate || 1.0;
                  const points = [];
                  for (let year = 0; year <= 10; year++) {
                    const x = 60 + (year * 70);
                    const soh = Math.max(50, 100 - (year * actualRate));
                    const y = 260 - ((soh - 50) * 4.8);
                    points.push(`${x},${y}`);
                  }
                  return points.join(' ');
                })()}
                fill="none"
                stroke="#f97316"
                strokeWidth="3"
                strokeDasharray="8 4"
              />
              
              {/* Data points on warranty line */}
              {(() => {
                const degradationRate = asset.warranty_degradation_rate;
                const points = [];
                for (let year = 0; year <= 10; year++) {
                  const x = 60 + (year * 70);
                  const soh = Math.max(50, 100 - (year * degradationRate));
                  const y = 260 - ((soh - 50) * 4.8);
                  points.push(
                    <circle key={`warranty-point-${year}`} cx={x} cy={y} r="4" fill="#10b981" />
                  );
                }
                return points;
              })()}
              
              {/* Data points on actual line */}
              {(() => {
                const actualRate = asset.actual_degradation_rate || 1.0;
                const points = [];
                for (let year = 0; year <= 10; year++) {
                  const x = 60 + (year * 70);
                  const soh = Math.max(50, 100 - (year * actualRate));
                  const y = 260 - ((soh - 50) * 4.8);
                  points.push(
                    <circle key={`actual-point-${year}`} cx={x} cy={y} r="4" fill="#f97316" />
                  );
                }
                return points;
              })()}
              
              {/* Current position marker */}
              {(() => {
                const commissionDate = new Date(asset.commissioning_date);
                const now = new Date();
                const yearsElapsed = Math.min(10, (now - commissionDate) / (365.25 * 24 * 60 * 60 * 1000));
                const x = 60 + (yearsElapsed * 70);
                const soh = Math.max(50, 100 - (yearsElapsed * asset.warranty_degradation_rate));
                const y = 260 - ((soh - 50) * 4.8);
                return (
                  <>
                    <circle cx={x} cy={y} r="8" fill="#3b82f6" stroke="white" strokeWidth="3" />
                    <text x={x} y={y - 15} fill="#3b82f6" fontSize="12" fontWeight="bold" textAnchor="middle">Current</text>
                  </>
                );
              })()}
              
              {/* Legend */}
              <rect x="580" y="30" width="180" height="90" fill="white" stroke="#6b7280" strokeWidth="1" />
              <text x="590" y="50" fill="#000" fontSize="11" fontWeight="bold">Legend:</text>
              
              <line x1="590" y1="68" x2="630" y2="68" stroke="#10b981" strokeWidth="3" />
              <text x="640" y="72" fill="#6b7280" fontSize="10">Warranty ({asset.warranty_degradation_rate}%/yr)</text>
              
              <line x1="590" y1="88" x2="630" y2="88" stroke="#f97316" strokeWidth="3" strokeDasharray="8 4" />
              <text x="640" y="92" fill="#6b7280" fontSize="10">Actual ({asset.actual_degradation_rate || 1.0}%/yr)</text>
              
              <circle cx="610" cy="105" r="5" fill="#3b82f6" />
              <text x="640" y="109" fill="#6b7280" fontSize="10">Current Position</text>
            </svg>
            <div className="text-sm text-gray-600 mt-4 text-center">
              <p className="font-medium">Projected SOH after 10 years: {Math.max(50, 100 - (10 * asset.warranty_degradation_rate)).toFixed(1)}%</p>
              <p className="text-xs mt-1">Based on {asset.warranty_degradation_rate}% annual degradation rate from commissioning date</p>
            </div>
          </CardContent>
        </Card>

        {/* Charts */}
        {telemetry.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Voltage Trend Chart */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-black">Voltage Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 rounded-lg p-4 h-[250px] relative">
                  <svg width="100%" height="100%" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
                    {/* Grid */}
                    <defs>
                      <pattern id="voltageGrid" width="60" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 40" fill="none" stroke="#2a2a2a" strokeWidth="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="600" height="200" fill="url(#voltageGrid)" />
                    
                    {/* Voltage line */}
                    <polyline
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="2"
                      points={telemetry.slice(0, 30).map((d, i) => {
                        const x = (i / 29) * 580 + 10;
                        const minV = Math.min(...telemetry.slice(0, 30).map(t => t.voltage));
                        const maxV = Math.max(...telemetry.slice(0, 30).map(t => t.voltage));
                        const y = 190 - ((d.voltage - minV) / (maxV - minV)) * 170;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                    
                    {/* Axes labels */}
                    <text x="10" y="15" fill="#9ca3af" fontSize="10">
                      {Math.max(...telemetry.slice(0, 30).map(t => t.voltage)).toFixed(1)}V
                    </text>
                    <text x="10" y="195" fill="#9ca3af" fontSize="10">
                      {Math.min(...telemetry.slice(0, 30).map(t => t.voltage)).toFixed(1)}V
                    </text>
                    <text x="550" y="195" fill="#9ca3af" fontSize="10">Now</text>
                    <text x="10" y="195" fill="#9ca3af" fontSize="10">-150m</text>
                  </svg>
                </div>
              </CardContent>
            </Card>

            {/* Temperature Trend Chart */}
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-black">Temperature Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-100 rounded-lg p-4 h-[250px] relative">
                  <svg width="100%" height="100%" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
                    {/* Grid */}
                    <defs>
                      <pattern id="tempGrid" width="60" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 40" fill="none" stroke="#2a2a2a" strokeWidth="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="600" height="200" fill="url(#tempGrid)" />
                    
                    {/* Temperature line */}
                    <polyline
                      fill="none"
                      stroke="#14b8a6"
                      strokeWidth="2"
                      points={telemetry.slice(0, 30).map((d, i) => {
                        const x = (i / 29) * 580 + 10;
                        const minT = Math.min(...telemetry.slice(0, 30).map(t => t.temperature));
                        const maxT = Math.max(...telemetry.slice(0, 30).map(t => t.temperature));
                        const y = 190 - ((d.temperature - minT) / (maxT - minT)) * 170;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                    
                    {/* Axes labels */}
                    <text x="10" y="15" fill="#9ca3af" fontSize="10">
                      {Math.max(...telemetry.slice(0, 30).map(t => t.temperature)).toFixed(1)}°C
                    </text>
                    <text x="10" y="195" fill="#9ca3af" fontSize="10">
                      {Math.min(...telemetry.slice(0, 30).map(t => t.temperature)).toFixed(1)}°C
                    </text>
                    <text x="550" y="195" fill="#9ca3af" fontSize="10">Now</text>
                    <text x="10" y="195" fill="#9ca3af" fontSize="10">-150m</text>
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">Loading charts...</p>
            </CardContent>
          </Card>
        )}

        {/* SOC History Floating Bar Chart - 100 Days */}
        {chartsReady && (
          <Card className="bg-white/5 border-white/10 mt-6">
            <CardHeader>
              <CardTitle className="text-black flex items-center justify-between">
                State of Charge - Daily Range (Last 100 Days)
                <Battery className="w-5 h-5 text-emerald-500" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              {socHistory.length > 0 ? (
              <>
                <div style={{ width: '100%', height: '300px', position: 'relative' }}>
                  <svg width="100%" height="300" viewBox="0 0 1000 300" preserveAspectRatio="xMidYMid meet">
                    {/* Grid */}
                    <defs>
                      <pattern id="socGrid" width="50" height="50" patternUnits="userSpaceOnUse">
                        <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                      </pattern>
                    </defs>
                    <rect width="1000" height="300" fill="url(#socGrid)" />
                    
                    {/* Y-axis labels */}
                    <text x="10" y="20" fill="#6b7280" fontSize="12" fontWeight="bold">100%</text>
                    <text x="10" y="80" fill="#6b7280" fontSize="12">75%</text>
                    <text x="10" y="155" fill="#6b7280" fontSize="12">50%</text>
                    <text x="10" y="230" fill="#6b7280" fontSize="12">25%</text>
                    <text x="10" y="285" fill="#6b7280" fontSize="12" fontWeight="bold">0%</text>
                    
                    {/* Floating bars - show every 5th day to avoid overcrowding */}
                    {socHistory.filter((_, i) => i % 5 === 0).map((day, i) => {
                      const x = 60 + (i * 50);
                      const yHigh = 270 - (day.soc_high / 100) * 250;
                      const yLow = 270 - (day.soc_low / 100) * 250;
                      const barHeight = yLow - yHigh;
                      
                      return (
                        <g key={day.date}>
                          {/* Bar */}
                          <rect
                            x={x}
                            y={yHigh}
                            width="30"
                            height={Math.max(barHeight, 2)}
                            fill="#10b981"
                            opacity="0.7"
                            stroke="#059669"
                            strokeWidth="1"
                          />
                          {/* High marker */}
                          <line x1={x} y1={yHigh} x2={x + 30} y2={yHigh} stroke="#059669" strokeWidth="2" />
                          {/* Low marker */}
                          <line x1={x} y1={yLow} x2={x + 30} y2={yLow} stroke="#059669" strokeWidth="2" />
                          
                          {/* Date label every 10th bar */}
                          {i % 2 === 0 && (
                            <text 
                              x={x + 15} 
                              y="295" 
                              fill="#6b7280" 
                              fontSize="9" 
                              textAnchor="middle"
                              transform={`rotate(-45, ${x + 15}, 295)`}
                            >
                              {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </text>
                          )}
                        </g>
                      );
                    })}
                    
                    {/* Legend */}
                    <text x="880" y="30" fill="#6b7280" fontSize="11" fontWeight="bold">Legend:</text>
                    <rect x="880" y="40" width="15" height="15" fill="#10b981" opacity="0.7" stroke="#059669" />
                    <text x="900" y="52" fill="#6b7280" fontSize="10">Daily Range</text>
                    <line x1="880" y1="60" x2="895" y2="60" stroke="#059669" strokeWidth="2" />
                    <text x="900" y="65" fill="#6b7280" fontSize="10">High/Low</text>
                  </svg>
                </div>
                <div className="mt-4 text-xs text-gray-600 text-center">
                  <p>Bars show daily SOC range. Top line = high, bottom line = low.</p>
                  <p className="mt-1">Data points: every 5th day shown for clarity (100 days total)</p>
                </div>
              </>
              ) : (
                <div className="py-12 text-center">
                  <p className="text-gray-600">Loading SOC history data...</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* 5-Day Weather Forecast Card */}
        {weatherData && (
          <Card className="bg-white border-gray-200 mt-6">
            <CardHeader>
              <CardTitle className="text-black">5-Day Weather Forecast - {weatherData.location}</CardTitle>
            </CardHeader>
            <CardContent>
              <svg width="100%" height="400" viewBox="0 0 1000 400" preserveAspectRatio="xMidYMid meet">
                {/* Background */}
                <defs>
                  <pattern id="weatherGrid" width="50" height="50" patternUnits="userSpaceOnUse">
                    <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e5e7eb" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="1000" height="400" fill="url(#weatherGrid)" />
                
                {/* Extreme temperature zones */}
                <rect x="60" y="20" width="900" height="40" fill="#fee2e2" opacity="0.3" />
                <rect x="60" y="340" width="900" height="40" fill="#dbeafe" opacity="0.3" />
                
                {/* Y-axis (Temperature) */}
                <line x1="60" y1="20" x2="60" y2="380" stroke="#6b7280" strokeWidth="2" />
                <text x="15" y="25" fill="#ef4444" fontSize="14" fontWeight="bold">50°C</text>
                <text x="15" y="65" fill="#000" fontSize="14" fontWeight="bold">40°C</text>
                <text x="15" y="125" fill="#6b7280" fontSize="13">30°C</text>
                <text x="15" y="185" fill="#6b7280" fontSize="13">20°C</text>
                <text x="15" y="245" fill="#6b7280" fontSize="13">10°C</text>
                <text x="15" y="305" fill="#6b7280" fontSize="13">0°C</text>
                <text x="10" y="345" fill="#3b82f6" fontSize="14" fontWeight="bold">-5°C</text>
                <text x="10" y="385" fill="#3b82f6" fontSize="14" fontWeight="bold">-10°C</text>
                
                {/* Threshold lines */}
                <line x1="60" y1="60" x2="960" y2="60" stroke="#ef4444" strokeWidth="2" strokeDasharray="5 5" />
                <line x1="60" y1="340" x2="960" y2="340" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5 5" />
                
                {/* X-axis (Days) */}
                <line x1="60" y1="380" x2="960" y2="380" stroke="#6b7280" strokeWidth="2" />
                
                {/* Temperature bars and data */}
                {weatherData.forecast.map((day, index) => {
                  const x = 180 + (index * 160);
                  const tempScale = 6; // pixels per degree
                  const baselineY = 340; // -5°C line
                  
                  const yMax = baselineY - ((day.temp_max + 5) * tempScale);
                  const yMin = baselineY - ((day.temp_min + 5) * tempScale);
                  const barHeight = Math.abs(yMin - yMax);
                  
                  // Determine color based on extreme temps
                  let barColor = "#10b981"; // Green for normal
                  let strokeColor = "#059669";
                  if (day.temp_max > 40) {
                    barColor = "#ef4444"; // Red for hot
                    strokeColor = "#dc2626";
                  } else if (day.temp_min < -5) {
                    barColor = "#3b82f6"; // Blue for cold
                    strokeColor = "#2563eb";
                  }
                  
                  return (
                    <g key={index}>
                      {/* Temperature bar */}
                      <rect 
                        x={x - 40} 
                        y={yMax} 
                        width="80" 
                        height={barHeight} 
                        fill={barColor} 
                        opacity="0.7"
                        stroke={strokeColor}
                        strokeWidth="2"
                      />
                      {/* Max temperature value */}
                      <text x={x} y={yMax - 10} fill="#000" fontSize="16" textAnchor="middle" fontWeight="bold">
                        {day.temp_max.toFixed(1)}°C
                      </text>
                      {/* Min temperature value */}
                      <text x={x} y={yMin + 25} fill="#374151" fontSize="14" textAnchor="middle" fontWeight="bold">
                        {day.temp_min.toFixed(1)}°C
                      </text>
                      
                      {/* Date label */}
                      <text x={x} y="395" fill="#000" fontSize="14" textAnchor="middle" fontWeight="bold">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </text>
                      <text x={x} y="410" fill="#6b7280" fontSize="12" textAnchor="middle">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </text>
                      
                      {/* Weather condition */}
                      <text x={x} y={Math.min(yMax, yMin) - 30} fill="#6b7280" fontSize="11" textAnchor="middle">
                        {day.condition}
                      </text>
                      
                      {/* Alert indicator if extreme */}
                      {(day.temp_max > 40 || day.temp_min < -5) && (
                        <text x={x} y={yMax - 25} fill="#ef4444" fontSize="20" textAnchor="middle">⚠</text>
                      )}
                    </g>
                  );
                })}
                
                {/* Legend */}
                <rect x="700" y="40" width="240" height="120" fill="white" stroke="#6b7280" strokeWidth="1" />
                <text x="710" y="60" fill="#000" fontSize="13" fontWeight="bold">Temperature Thresholds:</text>
                <line x1="710" y1="75" x2="750" y2="75" stroke="#ef4444" strokeWidth="2" strokeDasharray="5 5" />
                <text x="760" y="80" fill="#6b7280" fontSize="12">Above 40°C - Critical</text>
                <line x1="710" y1="100" x2="750" y2="100" stroke="#3b82f6" strokeWidth="2" strokeDasharray="5 5" />
                <text x="760" y="105" fill="#6b7280" fontSize="12">Below -5°C - Warning</text>
                <rect x="710" y="115" width="25" height="15" fill="#10b981" opacity="0.7" />
                <text x="760" y="127" fill="#6b7280" fontSize="12">Normal Range</text>
                <text x="710" y="150" fill="#ef4444" fontSize="13" fontWeight="bold">
                  {weatherData.alerts_generated?.length > 0 ? `⚠ ${weatherData.alerts_generated.length} Alert(s) Generated` : '✓ No Extreme Temps'}
                </text>
              </svg>
              <div className="mt-4 text-sm text-gray-700">
                <div className="grid grid-cols-5 gap-4">
                  {weatherData.forecast.map((day, index) => (
                    <div key={index} className="text-center p-2 bg-gray-50 rounded">
                      <div className="font-semibold text-xs text-gray-600 mb-1">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className="text-xs text-gray-600">Humidity: {day.humidity}%</div>
                      <div className="text-xs text-gray-600">Wind: {day.wind_speed} km/h</div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {telemetry.length === 0 && !loading && (
          <Card className="bg-white/5 border-white/10 mt-6">
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">No telemetry data available</p>
              <Button
                onClick={generateTelemetry}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                data-testid="generate-telemetry-btn"
              >
                Generate Sample Data
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="mt-6 flex space-x-4">
          <Button
            onClick={() => navigate(`/digital-twin/${id}`)}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            data-testid="view-digital-twin-btn"
          >
            View Digital Twin
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailPage;
